// services/guest-link.service.ts
import { inject, Injectable } from '@angular/core';
import { RoutingService } from '../routing.service';
import { ActiveUserStore } from '../../stores/user/active-user.state';
import { ConfirmationDialogService } from '../confirmation-dialog.service';

@Injectable({
  providedIn: 'root',
})
export class GuestLinkService {
  readonly userStore = inject(ActiveUserStore);
  constructor(
    private routingService: RoutingService,
    private confirmationDialogService: ConfirmationDialogService
  ) {}

  /**
   * Generates a cryptographically secure random token as a hexadecimal string.
   *
   * @return {string} A 64-character hexadecimal string representing the generated token.
   */
  generateToken(): string {
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join(
      ''
    );
  }

  /**
   * Constructs and returns a guest link URL based on the provided token.
   *
   * @param {string} token - The unique token used to generate the guest link URL.
   * @return {string} The complete guest link URL.
   */
  getGuestLinkUrl(token: string): string {
    return (
      window.location.origin +
      this.routingService.getBookmarkRoute(
        token,
        'guestView',
        this.userStore.commune()
      )
    );
  }

  /**
   * Copies text (url) to clipboard
   */
  async copyToClipboard(text: string): Promise<boolean> {
    try {
      await navigator.clipboard.writeText(text);
      this.copyFeedback(true);
      return true;
    } catch (err) {
      console.error('Failed to copy:', err);
      this.copyFeedback(false);
      return false;
    }
  }

  /**
   * Copies text (url) to clipboard
   */
  async copyPasswordToClipboard(text: string): Promise<boolean> {
    try {
      await navigator.clipboard.writeText(text);
      this.copyPasswordFeedback(true);
      return true;
    } catch (err) {
      this.copyPasswordFeedback(false);
      return false;
    }
  }

  /**
   * Copies the guest link associated with the provided token to the clipboard.
   *
   * @param {string} token - The token used to generate the guest link.
   * @return {Promise<void>} A promise that resolves when the guest link is successfully copied to the clipboard.
   */
  async copyGuestLink(token: string) {
    const url = this.getGuestLinkUrl(token);
    await this.copyToClipboard(url);
  }

  /**
   * Provides feedback to the user regarding the success or failure of copying a link.
   *
   * @param {boolean} success - Indicates whether the operation to copy the link was successful or not.
   * @return {void} This method does not return a value.
   */
  copyFeedback(success: boolean): void {
    if (success) {
      this.confirmationDialogService.showMessage(
        'Link kopiert',
        'Der Gast-Link wurde in die Zwischenablage kopiert'
      );
    } else {
      this.confirmationDialogService.addErrorMessage(
        'Der Link konnte nicht kopiert werden',
        'Fehler'
      );
    }
  }

  /**
   * Provides feedback to the user regarding the success or failure of copying a password to the clipboard.
   * Displays a confirmation dialog with either a success or error message.
   *
   * @param {boolean} success - Indicates whether the password copy operation was successful.
   *                            If true, a success message is shown. If false, an error message is displayed.
   * @return {void} Does not return any value.
   */
  copyPasswordFeedback(success: boolean): void {
    if (success) {
      this.confirmationDialogService.showMessage(
        'Passwort kopiert',
        'Das Passwort des Gast-Links wurde in die Zwischenablage kopiert'
      );
    } else {
      this.confirmationDialogService.addErrorMessage(
        'Das Passwort konnte nicht kopiert werden',
        'Fehler'
      );
    }
  }

  /**
   * Opens a guest link in a new browser tab or window.
   *
   * @param {string} token - A token used to authenticate or generate the guest link.
   * @return {void} This method does not return a value.
   */
  openGuestLink(token: string): void {
    const url = this.getGuestLinkUrl(token);
    window.open(url, '_blank');
  }
}
